/**
 * Тост и его хост (§2.3 «Тост», §12.6, §13).
 *
 * Очередь: показываются последние `max` тостов (по умолчанию 3), стопкой вверх
 * от нижнего края. Автозакрытие — успех 4 с, нейтральный 6 с, ошибка 8 с;
 * тост с действием живёт 8 с, иначе «Вернуть» не успеть нажать. Ховер и фокус
 * останавливают таймер (§2.3).
 *
 * Роль у каждого тоста своя: нейтральный и успех — `status` (aria-live polite),
 * ошибка — `alert` (assertive). Поэтому роль стоит на тосте, а не на хосте
 * (§13, «динамические сообщения»).
 *
 * Автозакрытие можно выключить (`autoDismiss={false}`), если таймером уже
 * управляет стор: `app-store.toast()` снимает тост сам.
 */
import { useEffect, useRef, useState } from 'react'
import type { CSSProperties } from 'react'
import { IconClose } from './icons'
import './Toast.css'

export type ToastKind = 'info' | 'success' | 'error'

export interface ToastAction {
  label: string
  onClick: () => void
}

export interface ToastItem {
  id: number
  kind: ToastKind
  text: string
  action?: ToastAction
}

/** Время жизни по варианту, мс (§2.3 и задача: успех 4 с, ошибка 8 с). */
const LIFETIME: Record<ToastKind, number> = {
  success: 4000,
  info: 6000,
  error: 8000,
}
/** Тост с действием живёт дольше: «Вернуть» надо успеть нажать (§2.3). */
const LIFETIME_WITH_ACTION = 8000

export interface ToastProps {
  toast: ToastItem
  onDismiss: (id: number) => void
  /** Закрывать по таймеру. False — таймером управляет стор. */
  autoDismiss?: boolean
  closeLabel?: string
}

export function Toast({ toast, onDismiss, autoDismiss = true, closeLabel = 'Закрыть' }: ToastProps) {
  const [paused, setPaused] = useState(false)
  const dismissRef = useRef(onDismiss)
  dismissRef.current = onDismiss

  const lifetime = toast.action ? LIFETIME_WITH_ACTION : LIFETIME[toast.kind]

  useEffect(() => {
    if (!autoDismiss || paused) return
    const timer = setTimeout(() => dismissRef.current(toast.id), lifetime)
    return () => clearTimeout(timer)
  }, [autoDismiss, paused, lifetime, toast.id])

  return (
    <div
      className={`toast toast--${toast.kind} t-cell`}
      role={toast.kind === 'error' ? 'alert' : 'status'}
      aria-live={toast.kind === 'error' ? 'assertive' : 'polite'}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
    >
      <span className="toast__text">{toast.text}</span>
      {toast.action ? (
        <button type="button" className="toast__action t-cell" onClick={toast.action.onClick}>
          {toast.action.label}
        </button>
      ) : null}
      <button
        type="button"
        className="toast__close"
        aria-label={closeLabel}
        onClick={() => onDismiss(toast.id)}
      >
        <IconClose size={16} />
      </button>
    </div>
  )
}

export interface ToastHostProps {
  toasts: ToastItem[]
  onDismiss: (id: number) => void
  /** Одновременно на экране — не больше трёх (§2.3). */
  max?: number
  /** Высота дока черновика в px: тосты встают на 16 px выше него. */
  offset?: number
  autoDismiss?: boolean
}

export function ToastHost({
  toasts,
  onDismiss,
  max = 3,
  offset = 0,
  autoDismiss = true,
}: ToastHostProps) {
  /* Видны последние: свежее сообщение важнее того, что человек уже прочитал. */
  const visible = toasts.slice(-max)

  return (
    <div
      className="toast-host"
      /* Смещение над доком отдаём в CSS переменной: вычисление остаётся в CSS. */
      style={{ '--toast-host-offset': `${offset}px` } as CSSProperties}
    >
      {visible.map((toast) => (
        <Toast key={toast.id} toast={toast} onDismiss={onDismiss} autoDismiss={autoDismiss} />
      ))}
    </div>
  )
}
